import React, { useState } from 'react'
import { Form, ListGroupItem } from 'react-bootstrap'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'

import { IRegistration } from '../../../store/registrations/interfaces'
import { updateRegistrationCustomAnswer } from '../../../store/registrations/registrations'
import { AddValidatorFunc } from '../../../hooks/validators'
import { CustomQuestion as _CustomQuestion, CustomQuestionType } from '../../../store/events/interfaces'
import { useAppDispatch, useAppSelector } from '../../../hooks'
import { linkify } from '../../../utils'
import { IToast, ToastType } from '../../../store/misc/interfaces'

export const CustomQuestionToast = {
  title: 'Antworten übernommen',
  body: 'Bitte überprüfen Sie, ob die Antworten auf die Fragen auch für diesen Teilnehmer zutreffen.',
  type: ToastType.Warning,
  duration: 5000,
} as IToast

const CustomQuestion = (props: {
  registration: IRegistration
  question: _CustomQuestion
  editable: boolean
  setError: (prevState: boolean) => void
  addValidator?: AddValidatorFunc
}) => {
  const { registration, question, editable, setError, addValidator } = props

  const dispatch = useAppDispatch()

  const answer = useAppSelector(state => {
    return state.registrations.list.find(otherReg => otherReg.event.event_id === registration.event.event_id)?.customAnswers.find(answer => answer.id === question.id)
  })

  const { loadedViaCode, loadedViaFamilyToken } = useAppSelector((state) => state.misc)
  const { originalRegistrationIds } = useAppSelector(
    (state) => state.participant.data,
  )

  const isOriginalReg = originalRegistrationIds?.includes(
    registration.event.event_id,
  )

  const originalCustomAnswers = useAppSelector(
    (state) => state.participant.data.originalCustomAnswers,
  )

  const hasPreExistingAnswer = originalCustomAnswers?.some(
    (originalAnswer) =>
      originalAnswer.eventId === registration.event.event_id &&
      originalAnswer.questionId === question.id
  )

  const { register, trigger, getValues, formState: { errors } } = useForm<{
    answer: string
  }>({
    mode: 'onBlur',
    defaultValues: {
      answer: answer?.answer,
    },
    resolver: yupResolver(yup.object().shape({
      answer: yup
        .string()
        .test(
          'optionallyRequired',
          'Dieses Feld ist zwingend erforderlich.',
          val => !question.required || !!val,
        )
        .test(
          'validChoices',
          'Bitte wählen Sie eine der angegebenen Optionen aus.',
          val =>
            question.type !== CustomQuestionType.CHOICES ||
            (!question.required && !val) ||
            question.choices.some(choice => choice === val),
        )
        .test(
          'validYesNo',
          'Bitte wählen Sie eine der angegebenen Optionen aus.',
          val =>
            question.type !== CustomQuestionType.YESNO ||
            (!question.required && !val) ||
            ['Ja', 'Nein', 'ja', 'nein'].some(choice => choice === val),
        ),
    })),
  })

  const [isHighlighted, setIsHighlighted] = useState(loadedViaFamilyToken)

  if (editable && addValidator) {
    addValidator(`customQuestion-${question.id}-${registration.event.event_id}`, {
      trigger,
      formName: `CustomQuestion`,
    })
  }
  
  return (
    <ListGroupItem className={`m-0`}>
      <Form.Group>
        <Form.Label>
          {question.label}
          {question.required && editable && '*'}
        </Form.Label>
        {editable ? (
          <>
            <Form.Control
              {...register('answer', {
                onBlur: () => {
                  dispatch(
                    updateRegistrationCustomAnswer({
                      eventId: registration.event.event_id,
                      answer: {
                        id: question.id,
                        ...getValues(),
                      },
                    }),
                  )
                  setIsHighlighted(false)
                  trigger()
                },
                onChange: () => {
                  setIsHighlighted(false)
                }
              })}
              required={question.required}
              id={`answer-${question.id}-${registration.event.event_id}`}
              aria-label='Benutzerdefinierte Frage für diese Veranstaltung'
              //TODO: This works for navigation between stages, but redux time travel doesn't work!
              className={`${isHighlighted ? 'highlight-animation' : ''} 
                ${question.type !== CustomQuestionType.TEXT ? 'custom-select' : undefined}`
              }
              as={
                question.type === CustomQuestionType.TEXT
                  ? 'textarea'
                  : 'select'
              }
              isInvalid={!!errors.answer}
              // only disable when it doesn't have a preexisting answer on required questions
              disabled={hasPreExistingAnswer}
            >
              {question.type !== CustomQuestionType.TEXT ? (
                question.type === CustomQuestionType.YESNO ? (
                  <>
                    <option value='' />
                    <option value='Ja'>Ja</option>
                    <option value='Nein'>Nein</option>
                  </>
                ) : (
                  <>
                    <option />
                    {question.choices.map((choice, key) => (
                      <option key={key} value={choice}>
                        {choice}
                      </option>
                    ))}
                  </>
                )
              ) : null}
            </Form.Control>
            <Form.Text className='text-muted' dangerouslySetInnerHTML={{ __html: linkify(question.description) }}></Form.Text>
            <Form.Control.Feedback type='invalid'>
              {errors.answer && errors.answer.message}
            </Form.Control.Feedback>
          </>
        ) : (
          <>
            <br />
            {answer ? (
              <small className='font-weight-bold'>{answer.answer}</small>
            ) : (
              <small className='text-muted'>Kein Angabe.</small>
            )}
          </>
        )}
      </Form.Group>
    </ListGroupItem>
  )
}

export default CustomQuestion